import { Box, Button, Flex, Icon, Input, InputGroup, InputLeftElement, Spinner, Stack, Tag, Text } from "@chakra-ui/react";
import { debounce } from "lodash";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiSearch } from "react-icons/fi";
import {
  type MinimalOrgDto,
  OrganizationType,
  useSearchOrganizationsAndEntriesQuery,
} from "../../../../../../autogen/bff-api";
import { OrganizationTypeTag } from "../../../../../../common/organization/OrganizationTypeTag";
import { SupplierFilterBar } from "../../../../../../common/organization/supplier-filters/SupplierFilterBar";
import {
  isFilteringSuppliers,
  NO_SUPPLIER_FILTERS,
  supplierFilterSearchArgs,
  type SupplierFilters,
} from "../../../../../../common/organization/supplier-filters/supplierFilters";
import { SupplierRelationTags } from "../../../../../../common/organization/supplier-filters/SupplierRelationTags";

const RESULT_LIMIT = 50;
const ORGANIZATION_NUMBER_LENGTH = 9;

interface Props {
  invitedOrganizationIds: string[];
  excludedOrganizationIds: string[];
  onInvite: (organization: MinimalOrgDto) => void;
}

export const SupplierFilterPanel = ({ invitedOrganizationIds, excludedOrganizationIds, onInvite }: Props) => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<SupplierFilters>(NO_SUPPLIER_FILTERS);
  const [search, setSearch] = useState("");
  const [searched, setSearched] = useState("");
  const settleSearch = useMemo(() => debounce((text: string) => setSearched(text), 300), []);

  const compact = searched.replace(/\s/g, "");
  const numeric = compact.length > 0 && /^\d+$/.test(compact);
  const completeNumber = numeric && compact.length === ORGANIZATION_NUMBER_LENGTH;
  const incompleteNumber = numeric && !completeNumber;
  const filtering = isFilteringSuppliers(filters);
  const searching = searched.trim().length > 0;
  const looking = filtering || searching;

  const {
    currentData: data,
    isFetching,
    isError,
    refetch,
  } = useSearchOrganizationsAndEntriesQuery(
    {
      limit: RESULT_LIMIT,
      name: searching && !numeric ? searched.trim() : undefined,
      organizationNumber: completeNumber ? compact : undefined,
      type: filtering ? OrganizationType.Organization : undefined,
      excludeLinkedEntries: true,
      ...supplierFilterSearchArgs(filters),
      withRelations: true,
      excludeOwnOrganization: true,
    },
    { skip: !looking || incompleteNumber },
  );

  const relationsOf = useMemo(
    () => new Map((data?.relations ?? []).map((r) => [r.organizationId, r.relations])),
    [data],
  );
  const matches = (data?.organizations ?? []).filter((organization) => !excludedOrganizationIds.includes(organization.id));
  const failed = isError && !isFetching;

  const summary = (() => {
    if (!looking) return null;
    if (incompleteNumber) return t("The organization number must have 9 digits.");
    if (isFetching) return t("Searching …");
    if (failed) return t("Could not load suppliers. Try again.");
    if (!data) return null;
    if (matches.length === 0) return t("No suppliers match");
    if (data.count > data.organizations.length)
      return t("Showing {{shown}} of {{count}} suppliers", { shown: data.organizations.length, count: data.count });
    return t("{{count}} suppliers match", { count: matches.length });
  })();

  return (
    <Stack spacing="3" mb="3">
      <SupplierFilterBar
        value={filters}
        onChange={setFilters}
        search={
          <InputGroup size="sm" flex="1">
            <InputLeftElement pointerEvents="none">
              <Icon as={FiSearch} color="smMuted" />
            </InputLeftElement>
            <Input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                settleSearch(e.target.value);
              }}
              placeholder={t("Search by name or org. no.") as string}
              aria-label={t("Search by name or org. no.") as string}
              rounded="md"
            />
          </InputGroup>
        }
      />

      {!looking && (
        <Text fontSize="sm" color="smMuted">
          {t("Search for a supplier, or add a filter to see the suppliers that match.")}
        </Text>
      )}
      <Flex role="status" aria-live="polite" align="center" gap="2">
        {summary && (
          <>
            {isFetching && <Spinner size="xs" />}
            <Text fontSize="sm" fontWeight="semibold" color={failed || incompleteNumber ? "smError" : "smMuted"}>
              {summary}
            </Text>
            {failed && (
              <Button size="sm" variant="link" colorScheme="teal" onClick={() => refetch()}>
                {t("Try again")}
              </Button>
            )}
          </>
        )}
      </Flex>

      {looking && data && matches.length === 0 && filtering && (
        <Button size="sm" variant="outline" alignSelf="flex-start" onClick={() => setFilters(NO_SUPPLIER_FILTERS)}>
          {t("Reset filters")}
        </Button>
      )}
      {looking && !incompleteNumber && matches.length > 0 && (
        <Stack spacing="2" aria-label={t("Suppliers matching the search") as string}>
          {data && data.count > data.organizations.length && (
            <Text fontSize="sm" color="smMuted">
              {t("Narrow the search or the filters to see the rest.")}
            </Text>
          )}
          {matches.map((organization) => {
            const invited = invitedOrganizationIds.includes(organization.id);
            return (
              <Flex
                key={organization.id}
                justify="space-between"
                align="center"
                gap="3"
                borderWidth="1px"
                borderColor="smBorder"
                rounded="md"
                px="3"
                py="2"
              >
                <Box minW="0">
                  <Flex align="center" gap="2" wrap="wrap">
                    <Text fontSize="sm" fontWeight="medium">
                      {organization.name}
                    </Text>
                    {organization.type === OrganizationType.OrganizationEntry && (
                      <OrganizationTypeTag type={organization.type} />
                    )}
                  </Flex>
                  {organization.organizationNumber && (
                    <Text fontSize="xs" color="smMuted">
                      {organization.organizationNumber}
                    </Text>
                  )}
                  <SupplierRelationTags relations={relationsOf.get(organization.id) ?? []} mt="1" />
                </Box>
                {invited ? (
                  <Tag size="sm">{t("Invited")}</Tag>
                ) : (
                  <Button size="sm" colorScheme="teal" variant="outline" onClick={() => onInvite(organization)}>
                    {t("Invite")}
                  </Button>
                )}
              </Flex>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
};
